import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { 
  FileSpreadsheet, 
  Search, 
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Package,
  CheckCircle,
  XCircle,
  AlertCircle,
  Server,
  Monitor,
  ShieldAlert
} from 'lucide-react';

const InventoryReport = () => {
  const { can } = useAuth();
  const canRead = can('reports.inventory', 'read');
  
  // Estados para filtros
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filterOptions, setFilterOptions] = useState({ sites: [], empresas: [], estados: [], clientes: [] });
  const [selectedModules, setSelectedModules] = useState([]);
  const [filters, setFilters] = useState({
    site_id: '',
    empresa_id: '',
    status_id: '',
    cliente_id: ''
  });
  
  // Estados para columnas
  const [availableColumns] = useState([
    { key: 'tipo', label: 'Tipo', default: true },
    { key: 'clasificacion', label: 'Clasificación', default: true, assetsOnly: true },
    { key: 'asset_tag', label: 'Asset Tag / Nombre', default: true },
    { key: 'marca', label: 'Marca', default: true },
    { key: 'modelo', label: 'Modelo', default: true },
    { key: 'serial', label: 'Serial', default: false },
    { key: 'site_name', label: 'Site', default: true },
    { key: 'empresa_name', label: 'Empresa', default: true },
    { key: 'status_name', label: 'Estado', default: true },
    { key: 'cliente_name', label: 'Cliente', default: false, microOnly: true },
    { key: 'usuario_name', label: 'Usuario', default: false, microOnly: true },
    { key: 'legajo', label: 'Legajo', default: false, microOnly: true },
    { key: 'ip', label: 'IP', default: false, microOnly: true },
    { key: 'cpu', label: 'CPU', default: false, microOnly: true },
    { key: 'ram', label: 'RAM', default: false, microOnly: true },
    { key: 'cores', label: 'Cores', default: false, microOnly: true },
    { key: 'storage', label: 'Storage', default: false, microOnly: true },
    { key: 'mac', label: 'MAC', default: false, microOnly: true },
    { key: 'os_detected', label: 'SO Detectado', default: false, microOnly: true },
    { key: 'os_version', label: 'OS Build', default: false, microOnly: true },
    { key: 'fecha_compra', label: 'Fecha Compra', default: false },
    { key: 'comentarios', label: 'Comentarios', default: false },
  ]);
  const [selectedColumns, setSelectedColumns] = useState(
    availableColumns.filter(c => c.default).map(c => c.key)
  );
  
  // Estados para datos
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Cargar módulos y filtros al inicio
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [modulesRes, filtersRes] = await Promise.all([
          api.get('/reports/inventory/modules'),
          api.get('/reports/inventory/filters')
        ]);
        setCategories(modulesRes.data.categories || []);
        setFilterOptions(filtersRes.data || { sites: [], empresas: [], estados: [], clientes: [] });
      } catch (error) {
        toast.error('Error al cargar opciones');
        console.error(error);
      }
    };
    loadInitialData();
  }, []);

  // Obtener la categoría seleccionada
  const currentCategory = categories.find(c => c.id === selectedCategory);

  // Cambiar categoría - limpiar módulos seleccionados y columnas exclusivas.
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedModules([]);
    setPreviewData(null);
    // Limpiar filtro de cliente si cambiamos a Assets
    if (categoryId === 'assets') {
      setFilters(prev => ({ ...prev, cliente_id: '' }));
      // Quitar columnas que solo aplican a micro
      setSelectedColumns(prev => prev.filter(col => {
        const colConfig = availableColumns.find(c => c.key === col);
        return !colConfig?.microOnly;
      }));
      setSelectedColumns(prev => {
        const classificationColumn = availableColumns.find(c => c.key === 'clasificacion');
        if (!classificationColumn || prev.includes(classificationColumn.key)) return prev;
        const tipoIndex = prev.indexOf('tipo');
        return [
          ...prev.slice(0, tipoIndex + 1),
          classificationColumn.key,
          ...prev.slice(tipoIndex + 1),
        ];
      });
    } else {
      setSelectedColumns(prev => prev.filter(col => {
        const colConfig = availableColumns.find(c => c.key === col);
        return !colConfig?.assetsOnly;
      }));
    }
  };

  // Toggle módulo seleccionado
  const toggleModule = (moduleId) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  // Seleccionar todos los módulos de la categoría actual
  const selectAllModules = () => {
    if (currentCategory) {
      const allModuleIds = currentCategory.modules.map(m => m.id);
      const allSelected = allModuleIds.every(id => selectedModules.includes(id));
      if (allSelected) {
        setSelectedModules([]);
      } else {
        setSelectedModules(allModuleIds);
      }
    }
  };

  // Toggle columna
  const toggleColumn = (columnKey) => {
    const colConfig = availableColumns.find(c => c.key === columnKey);
    // No permitir seleccionar columnas microOnly si está en Assets
    if (colConfig?.microOnly && selectedCategory === 'assets') {
      return;
    }
    setSelectedColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(c => c !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Generar vista previa
  const generatePreview = useCallback(async (pageNum = 1) => {
    if (!selectedCategory) {
      toast.error('Selecciona una categoría primero');
      return;
    }
    if (selectedModules.length === 0) {
      toast.error('Selecciona al menos un módulo');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('modules', selectedModules.join(','));
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.cliente_id && selectedCategory === 'micro') {
        params.append('cliente_id', filters.cliente_id);
      }
      params.append('page', pageNum);
      params.append('page_size', pageSize);

      const response = await api.get(`/reports/inventory?${params.toString()}`);
      setPreviewData(response.data);
      setPage(pageNum);
    } catch (error) {
      toast.error('Error al generar vista previa');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedModules, filters]);

  // Exportar a Excel
  const exportToExcel = async () => {
    if (selectedModules.length === 0) {
      toast.error('Selecciona al menos un módulo');
      return;
    }
    
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('modules', selectedModules.join(','));
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.cliente_id && selectedCategory === 'micro') {
        params.append('cliente_id', filters.cliente_id);
      }
      if (selectedColumns.length > 0) {
        params.append('columns', selectedColumns.join(','));
      }

      const response = await api.get(`/reports/inventory/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const categoryName = selectedCategory === 'assets' ? 'infraestructura' : 'microinformatica';
      link.setAttribute('download', `inventario_${categoryName}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo descargado correctamente');
    } catch (error) {
      toast.error('Error al exportar');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Obtener badge de estado
  const getStatusBadge = (status) => {
    if (!status) return <Badge variant="outline">Sin estado</Badge>;
    const lower = status.toLowerCase();
    if (lower.includes('activo') || lower.includes('operativo')) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">{status}</Badge>;
    }
    if (lower.includes('baja') || lower.includes('inactivo')) {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{status}</Badge>;
    }
    if (lower.includes('reparac') || lower.includes('manten')) {
      return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{status}</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  // Columnas visibles según categoría
  const visibleColumns = availableColumns.filter(col => {
    if (col.microOnly && selectedCategory === 'assets') return false;
    if (col.assetsOnly && selectedCategory === 'micro') return false;
    return true;
  });

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="h-8 w-8 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-semibold">Inventario General</h1>
            <p className="text-sm text-muted-foreground">Genera un reporte consolidado de activos</p>
          </div>
        </div>

        {/* Sin permiso */}
        {!canRead && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="flex items-center gap-4 py-8">
              <ShieldAlert className="h-12 w-12 text-destructive" strokeWidth={1.5} />
              <div>
                <h3 className="text-lg font-medium text-destructive">Acceso Denegado</h3>
                <p className="text-sm text-muted-foreground">No tiene permisos para acceder a este reporte.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contenido con permiso */}
        {canRead && (
        <>
        {/* Categoría - Radio Buttons */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Categoría</CardTitle>
            <CardDescription>Selecciona una categoría (son mutuamente excluyentes)</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              value={selectedCategory} 
              onValueChange={handleCategoryChange}
              className="flex flex-wrap gap-6"
            >
              {categories.map(category => (
                <div key={category.id} className="flex items-center space-x-3">
                  <RadioGroupItem value={category.id} id={`cat-${category.id}`} />
                  <Label 
                    htmlFor={`cat-${category.id}`} 
                    className="flex items-center gap-2 cursor-pointer text-base"
                  >
                    {category.id === 'assets' ? (
                      <Server className="h-5 w-5 text-blue-400" strokeWidth={1.5} />
                    ) : (
                      <Monitor className="h-5 w-5 text-green-400" strokeWidth={1.5} />
                    )}
                    {category.name}
                    <span className="text-xs text-muted-foreground">({category.modules.length} módulos)</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Módulos - Solo si hay categoría seleccionada */}
        {currentCategory && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Módulos a incluir</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={selectAllModules}
                  className="text-xs"
                >
                  {currentCategory.modules.every(m => selectedModules.includes(m.id)) 
                    ? 'Deseleccionar todos' 
                    : 'Seleccionar todos'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {currentCategory.modules.map(module => (
                  <div key={module.id} className="flex items-center gap-2">
                    <Checkbox 
                      id={module.id}
                      checked={selectedModules.includes(module.id)}
                      onCheckedChange={() => toggleModule(module.id)}
                    />
                    <Label htmlFor={module.id} className="text-sm cursor-pointer">
                      {module.name}
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros adicionales */}
        {selectedCategory && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Filtros adicionales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid gap-4 ${selectedCategory === 'micro' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
                <div className="space-y-1">
                  <Label className="text-xs">Site</Label>
                  <Select value={filters.site_id || "all"} onValueChange={v => setFilters({...filters, site_id: v === 'all' ? '' : v})}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {filterOptions.sites.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Empresa</Label>
                  <Select value={filters.empresa_id || "all"} onValueChange={v => setFilters({...filters, empresa_id: v === 'all' ? '' : v})}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {filterOptions.empresas.map(e => (
                        <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <Select value={filters.status_id || "all"} onValueChange={v => setFilters({...filters, status_id: v === 'all' ? '' : v})}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {filterOptions.estados.map(e => (
                        <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Cliente solo para Micro */}
                {selectedCategory === 'micro' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Cliente</Label>
                    <Select value={filters.cliente_id || "all"} onValueChange={v => setFilters({...filters, cliente_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {filterOptions.clientes.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Columnas */}
        {selectedCategory && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Columnas a incluir</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {visibleColumns.map(col => (
                  <div key={col.key} className="flex items-center gap-2">
                    <Checkbox 
                      id={`col-${col.key}`}
                      checked={selectedColumns.includes(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                    />
                    <Label htmlFor={`col-${col.key}`} className="text-sm cursor-pointer">
                      {col.label}
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Botón generar */}
        {selectedCategory && (
          <div className="flex justify-center">
            <Button 
              onClick={() => generatePreview(1)} 
              disabled={loading || selectedModules.length === 0}
              className="gap-2"
              size="lg"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Generar Vista Previa
            </Button>
          </div>
        )}

        {/* Vista Previa */}
        {previewData && (
          <>
            {/* Tabla */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Vista Previa</CardTitle>
                  <CardDescription>Mostrando {previewData.items?.length || 0} de {previewData.total || 0} registros</CardDescription>
                </div>
                <Button 
                  onClick={exportToExcel} 
                  disabled={exporting || !previewData.total}
                  className="gap-2"
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Exportar a Excel
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {selectedColumns.map(colKey => {
                          const col = availableColumns.find(c => c.key === colKey);
                          if (col?.microOnly && selectedCategory === 'assets') return null;
                          if (col?.assetsOnly && selectedCategory === 'micro') return null;
                          return (
                            <th key={colKey} className="text-left py-2 px-3 font-medium text-muted-foreground">
                              {col?.label || colKey}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.items?.length === 0 ? (
                        <tr>
                          <td colSpan={selectedColumns.length} className="text-center py-8 text-muted-foreground">
                            No se encontraron registros
                          </td>
                        </tr>
                      ) : (
                        previewData.items?.map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/30">
                            {selectedColumns.map(colKey => {
                              const col = availableColumns.find(c => c.key === colKey);
                              if (col?.microOnly && selectedCategory === 'assets') return null;
                              if (col?.assetsOnly && selectedCategory === 'micro') return null;
                              return (
                                <td key={colKey} className="py-2 px-3">
                                  {colKey === 'status_name' ? (
                                    getStatusBadge(item[colKey])
                                  ) : (
                                    item[colKey] || '-'
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                {previewData.pages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-muted-foreground">
                      Página {page} de {previewData.pages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generatePreview(page - 1)}
                        disabled={page <= 1 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generatePreview(page + 1)}
                        disabled={page >= previewData.pages || loading}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
        </>
        )}
      </div>
    </Layout>
  );
};

export default InventoryReport;
